import { isInMobileApp } from '@/utils/chat';

/**
 * Attention for messages that arrive while the reader is elsewhere: a short chime and,
 * when allowed, a desktop notification that opens the thread. Everything here is best
 * effort and silent about failures (no audio context, no permission, no window focus).
 */

const SOUND_KEY = 'abol.chat.sound';
const DESKTOP_DISMISSED_KEY = 'abol.chat.desktopDismissed';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // private mode or blocked storage: the preference just does not stick
  }
};

/* ---------------------------------- Sound --------------------------------- */

export const isSoundEnabled = (): boolean => read(SOUND_KEY) !== 'off';

export const setSoundEnabled = (enabled: boolean): void => write(SOUND_KEY, enabled ? 'on' : 'off');

let audioContext: AudioContext | null = null;
let lastChimeAt = 0;

/** A soft two-note chime made with the Web Audio API, so no asset has to load. */
export const playChime = (): void => {
  if (!isSoundEnabled() || isInMobileApp()) return;
  const now = Date.now();
  if (now - lastChimeAt < 1500) return;
  lastChimeAt = now;
  try {
    const Context = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Context) return;
    const context: AudioContext = audioContext ?? new Context();
    audioContext = context;
    if (context.state === 'suspended') context.resume().catch(() => undefined);
    const start = context.currentTime;
    [[880, 0], [1174.66, 0.12]].forEach(([frequency, offset]) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.12, start + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.28);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start + offset);
      oscillator.stop(start + offset + 0.3);
    });
  } catch (e) {
    // no audio available
  }
};

/* --------------------------- Desktop notifications ------------------------ */

export const desktopNotificationsSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window && !isInMobileApp();

export const desktopPermission = (): NotificationPermission | 'unsupported' =>
  desktopNotificationsSupported() ? Notification.permission : 'unsupported';

/** Whether to offer the "enable desktop notifications" banner. */
export const shouldOfferDesktopNotifications = (): boolean =>
  desktopNotificationsSupported() && Notification.permission === 'default' && read(DESKTOP_DISMISSED_KEY) !== '1';

export const dismissDesktopOffer = (): void => write(DESKTOP_DISMISSED_KEY, '1');

export const requestDesktopPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!desktopNotificationsSupported()) return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (e) {
    return Notification.permission;
  }
};

const openNotifications = new Map<string, Notification>();

/**
 * Shows (or replaces) the notification for one thread. Only when the page is not
 * visible: a visible page shows the message itself.
 */
export const showDesktopNotification = (options: { tag: string; title: string; body: string; onClick: () => void }): void => {
  if (!desktopNotificationsSupported() || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;
  try {
    openNotifications.get(options.tag)?.close();
    const notification = new Notification(options.title, { body: options.body, tag: options.tag, icon: '/abol HR logo.png', silent: true });
    notification.onclick = () => {
      try {
        window.focus();
      } catch (e) {
        // some browsers refuse programmatic focus
      }
      options.onClick();
      notification.close();
    };
    notification.onclose = () => openNotifications.delete(options.tag);
    openNotifications.set(options.tag, notification);
    setTimeout(() => notification.close(), 8000);
  } catch (e) {
    // notification constructor can throw on some mobile browsers
  }
};

export const closeDesktopNotification = (tag: string): void => {
  openNotifications.get(tag)?.close();
  openNotifications.delete(tag);
};

/* ------------------------------ Document title ---------------------------- */

let baseTitle: string | null = null;

/** Prefixes the tab title with the unread count, like mail clients do. */
export const reflectUnreadInTitle = (count: number): void => {
  if (typeof document === 'undefined') return;
  const current = document.title.replace(/^\(\d+\+?\)\s*/, '');
  if (baseTitle === null || current !== baseTitle) baseTitle = current;
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${baseTitle}` : baseTitle;
};
